import { useRef, useState } from "react";
import { ExternalLink, FileText, Loader2, Plus, Sparkles, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Pill } from "@/components/common/States";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useFetch } from "@/lib/hooks";
import { errorMessage } from "@/lib/api";

const LIST_FIELDS = ["search_terms", "technical_capabilities", "target_markets", "ideal_customer_characteristics", "problems_solved", "positive_buying_signals",
  "exclusion_criteria", "typical_decision_makers", "competitors", "case_studies"];
const STATUS_TONE = { ok: "emerald", no_text: "amber", error: "rose", extracted: "emerald", failed: "rose" };

export function mergeDraft(existing, draft) {
  if (!existing) return draft;
  const out = { ...existing };
  LIST_FIELDS.forEach((f) => { out[f] = [...new Set([...(existing[f] || []), ...(draft[f] || [])])]; });
  ["description", "qualification_instructions"].forEach((f) => { if (!existing[f]) out[f] = draft[f]; });
  return out;
}

function Preview({ result }) {
  const { draft, numbered_sources: ns } = result;
  const cite = (f) => (draft.field_sources[f] || []).map((n) => <a key={n} href={ns[n - 1]?.url || undefined} target="_blank" rel="noreferrer" className="ml-1 font-mono text-[10px] text-primary">[{n}]</a>);
  const row = (f, v) => (v && (!Array.isArray(v) || v.length) ? (
    <div key={f} className="border-b py-2 last:border-0"><p className="eyebrow mb-1">{f.replace(/_/g, " ")}{cite(f)}</p>
      {Array.isArray(v) ? <ul className="list-disc pl-4 text-sm">{v.map((x, i) => <li key={i}>{x}</li>)}</ul> : <p className="text-sm">{v}</p>}</div>) : null);
  return (
    <div className="min-w-0 space-y-4" data-testid="ai-draft-preview">
      <div className="max-h-[38vh] min-w-0 overflow-y-auto break-words rounded-md border px-3">
        {row("name", draft.name)}{row("description", draft.description)}{LIST_FIELDS.map((f) => row(f, draft[f]))}{row("qualification_instructions", draft.qualification_instructions)}
      </div>
      {draft.not_found?.length > 0 && <p className="text-xs text-muted-foreground" data-testid="ai-draft-not-found">Not found in sources (left blank): {draft.not_found.join(", ").replace(/_/g, " ")}</p>}
      <div><p className="eyebrow mb-1">Sources read</p>
        {result.sources.map((s, i) => <div key={i} className="flex min-w-0 flex-wrap items-center gap-2 py-0.5 text-xs"><span className="shrink-0"><Pill tone={STATUS_TONE[s.status] || "slate"}>{s.status}</Pill></span><span className="shrink-0 text-muted-foreground">{s.kind.replace(/_/g, " ")}</span><span className="min-w-0 flex-1 truncate">{s.title || s.url}</span>{s.url && <a className="shrink-0" href={s.url} target="_blank" rel="noreferrer" aria-label="Open source"><ExternalLink className="h-3 w-3" /></a>}{s.error && <span className="w-full text-rose-500">{s.error}</span>}</div>)}
        <p className="mt-2 text-xs text-muted-foreground">Online research: {result.web_research === "used" ? "used (third-party results only inform competitors and market context)" : result.web_research === "not_connected" ? "not connected — add a Tavily key on Integrations to enable" : "skipped"}</p>
      </div>
    </div>
  );
}

function DatasheetUpload({ product, docs, setDocs }) {
  const { api } = useWorkspace();
  const input = useRef(null);
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    const form = new FormData();
    form.append("file", file); form.append("purpose", "product_document");
    if (product?.id) form.append("product_profile_id", product.id);
    setUploading(true);
    try { const { data } = await api.post("/documents", form); setDocs((d) => [...d, data]); }
    catch (e) { toast.error(errorMessage(e)); }
    finally { setUploading(false); if (input.current) input.current.value = ""; }
  };
  return (
    <div className="space-y-2 rounded-md border border-dashed p-3" data-testid="ai-draft-datasheets">
      <p className="text-xs text-muted-foreground">Datasheets or brochures (PDF, Word, text — up to 15 MB). A datasheet on its own is enough: AI reads the product name and maker from it and runs the same online research as a link.</p>
      {docs.map((d) => (
        <div key={d.id} className="flex items-center gap-2 text-xs" data-testid={`ai-draft-doc-${d.id}`}>
          <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /><span className="min-w-0 flex-1 truncate">{d.filename}</span><Pill tone={STATUS_TONE[d.extraction_status] || "slate"}>{d.extraction_status}</Pill>
          <button type="button" aria-label="Remove" onClick={() => setDocs(docs.filter((x) => x.id !== d.id))} data-testid={`ai-draft-doc-remove-${d.id}`}><X className="h-3.5 w-3.5" /></button>
        </div>
      ))}
      <input ref={input} type="file" accept=".pdf,.docx,.txt,.md,.csv" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} data-testid="ai-draft-doc-input" />
      {docs.length < 5 && <Button size="sm" variant="ghost" disabled={uploading} onClick={() => input.current?.click()} data-testid="ai-draft-doc-upload">{uploading ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Upload className="mr-1 h-3.5 w-3.5" />}Upload datasheet</Button>}
    </div>
  );
}

export function AiDraftDialog({ product, open, onOpenChange, onApply }) {
  const { api } = useWorkspace();
  const integrations = useFetch(open ? "/integrations" : null);
  const webOn = integrations.data?.find((i) => i.id === "web_research")?.status === "connected";
  const [urls, setUrls] = useState([""]);
  const [caseUrls, setCaseUrls] = useState([""]);
  const [docs, setDocs] = useState([]);
  const [web, setWeb] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const readyDocs = docs.filter((d) => d.extraction_status === "extracted");
  const canRun = urls.some((u) => /^https?:\/\//.test(u)) || readyDocs.length > 0;
  const run = async () => {
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/product-profiles/ai-draft", { urls: urls.filter((u) => /^https?:\/\//.test(u)), case_study_urls: caseUrls.filter((u) => /^https?:\/\//.test(u)), document_ids: docs.map((d) => d.id), product_profile_id: product?.id || null, use_web_research: web });
      setResult(data);
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setBusy(false); }
  };
  const close = (o) => { if (!o) { setResult(null); setUrls([""]); setCaseUrls([""]); setDocs([]); } onOpenChange(o); };
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl [&>*]:min-w-0" data-testid="ai-draft-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-xl"><Sparkles className="h-5 w-5 text-primary" />Draft with AI{product ? ` · ${product.name}` : ""}</DialogTitle>
          <DialogDescription>Link the product web page(s), upload a datasheet, or both. AI reads them, any datasheets linked from the pages{product ? ", this profile's uploaded documents" : ""} and, if connected, online research. Nothing is saved until you review it.</DialogDescription>
        </DialogHeader>
        {!result && (
          <div className="space-y-3">
            {urls.map((u, i) => (
              <div key={i} className="flex gap-2">
                <Input value={u} placeholder="https://www.adaptiverecognition.com/products/…" onChange={(e) => setUrls(urls.map((x, j) => (j === i ? e.target.value : x)))} data-testid={`ai-draft-url-${i}`} />
                {urls.length > 1 && <Button size="icon" variant="ghost" aria-label="Remove page" data-testid={`ai-draft-remove-url-${i}`} onClick={() => setUrls(urls.filter((_, j) => j !== i))}><X className="h-4 w-4" /></Button>}
              </div>
            ))}
            {urls.length < 3 && <Button size="sm" variant="ghost" onClick={() => setUrls([...urls, ""])} data-testid="ai-draft-add-url"><Plus className="mr-1 h-3.5 w-3.5" />Add another page</Button>}
            <DatasheetUpload product={product} docs={docs} setDocs={setDocs} />
            <div className="space-y-2 rounded-md border border-dashed p-3">
              <p className="text-xs text-muted-foreground">Case-study pages are found automatically from the product page{webOn ? " and an online search of the same website" : ""}. Add specific ones here (optional).</p>
              {caseUrls.map((u, i) => (
                <div key={i} className="flex gap-2">
                  <Input value={u} placeholder="https://…/case-study/…" onChange={(e) => setCaseUrls(caseUrls.map((x, j) => (j === i ? e.target.value : x)))} data-testid={`ai-draft-case-url-${i}`} />
                  {caseUrls.length > 1 && <Button size="icon" variant="ghost" aria-label="Remove case study" data-testid={`ai-draft-remove-case-url-${i}`} onClick={() => setCaseUrls(caseUrls.filter((_, j) => j !== i))}><X className="h-4 w-4" /></Button>}
                </div>
              ))}
              {caseUrls.length < 3 && <Button size="sm" variant="ghost" onClick={() => setCaseUrls([...caseUrls, ""])} data-testid="ai-draft-add-case-url"><Plus className="mr-1 h-3.5 w-3.5" />Add case study</Button>}
            </div>
            <div className="flex items-center gap-2"><Switch id="ai-web" checked={web && webOn} disabled={!webOn} onCheckedChange={setWeb} data-testid="ai-draft-web-toggle" /><Label htmlFor="ai-web" className="text-sm">Online research {webOn ? "" : "(Tavily not connected)"}</Label></div>
            <Button onClick={run} disabled={busy || !canRun} data-testid="ai-draft-run">{busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Reading sources and drafting — up to a minute…</> : <><Sparkles className="mr-2 h-4 w-4" />Draft with AI</>}</Button>
          </div>
        )}
        {result && (
          <>
            <Preview result={result} />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setResult(null)} data-testid="ai-draft-again">Try again</Button>
              <Button onClick={() => { onApply(result.draft, docs.map((d) => d.id)); close(false); }} data-testid="ai-draft-apply">Review in form</Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
